import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core'
import { takeUntil, Subject, take, filter, combineLatest, map, switchMap, shareReplay, iif, forkJoin } from 'rxjs'
import {
  GIFT_SUBSCRIPTION_OFFERS_ANALYTICS_TAG,
  GiftSubscriptionAnalyticsService
} from 'src/app/gift-subscription/gift-subscription-analytics.service'
import {
  FEAT_SINGLE_CLICK_MOBILE_PLANS_ID,
  FEAT_SINGLE_CLICK_MOBILE_PLANS_VARIATION,
  FEAT_SINGLE_CLICK_DESKTOP_PLANS_ID,
  FEAT_SINGLE_CLICK_DESKTOP_PLANS_VARIATION,
  FEAT_OLD_ENTITLEMENTS_WRESTLING,
  FEAT_OLD_ENTITLEMENTS_WRESTLING_VARIATION,
  EXP_NEW_PLANS_PAGE_ID,
  EXP_NEW_PLANS_PAGE_VARIATION_1,
  EXP_NEW_PLANS_PAGE_TOP_VERTICALS_ID,
  EXP_NEW_PLANS_PAGE_TOP_VERTICALS_VARIATION_1,
  FEAT_GWP_ID,
  FEAT_GWP_VARIATION,
  FEAT_UNIFIED_LAYOUT_ID,
  FEAT_UNIFIED_LAYOUT_VARIATION
} from 'src/app/shared/experimentation/experiment.model'
import { ExperimentationService } from 'src/app/shared/experimentation/experimentation.service'
import { GiftService } from 'src/app/shared/services/gift.service'
import { FunnelRouteData, LEGACY_PLANS_SEGMENT_TAG, getFunnelProperties } from '../funnel-analytics.utility'
import { ActivatedRoute, Router } from '@angular/router'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import { AuthService } from 'src/app/singleton-services/auth/auth.service'
import { ProductService } from 'src/app/shared/services/product.service'
import { OffersService } from 'src/app/singleton-services/offers.service'
import { CouponStatusUtility } from '../coupon/coupon-status.utility'
import { trackById } from 'src/app/shared/utility-functions/track-by-id.utility'
import { getBillingPlanLabel, mapPlansToOfferSelectionCardModel } from './legacy-plans/legacy-plans.utility'
import { OfferSelectionCardModel, TextModel, TextModelStyle } from '@flocasts/experience-service-types'
import { VerticalService } from '../../singleton-services/vertical.service'
import { Plan } from '@flocasts/flosports30-types/dist/subscriptions'
import { PartialsDataService } from '../../singleton-services/partials/partials-data.service'
import { SiteIds } from 'src/app/shared/models/site.model'
import { someJoin } from 'src/app/shared/rx-pipes/some-join'

export const filterInstallmentOffers = (offers: OfferSelectionCardModel[]): OfferSelectionCardModel[] => {
  return offers.filter(offer => !offer.offerMetadata?.isInstallment)
}

@Component({
  selector: 'flo-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlansComponent implements OnInit, OnDestroy {
  constructor(
    private readonly ar: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly couponStatusUtility: CouponStatusUtility,
    private readonly experimentService: ExperimentationService,
    private readonly giftService: GiftService,
    private readonly giftSubAnalytics: GiftSubscriptionAnalyticsService,
    private readonly offersService: OffersService,
    private readonly productService: ProductService,
    private readonly router: Router,
    private readonly segment: SegmentService,
    private readonly verticalService: VerticalService,
    private readonly partialsDataService: PartialsDataService
  ) {}

  private readonly ngOnDestroy$ = new Subject<void>()
  legacyPlans: Plan[] = []
  public selectedOffer$ = this.offersService.selectedOffer$
  public isInGiftSubscription$ = this.giftService.isInGiftSubscription$.pipe(shareReplay(1))
  public isEmbeddedPurchase$ = this.ar.queryParams.pipe(map(queryParams => !!queryParams.embeddedPurchase))
  public siteId$ = this.verticalService.siteId$

  // TODO: refactor to subscribe to the ngOnInit instead
  ngOnInit() {
    this.isInGiftSubscription$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(isInGiftSubscription => {
      if (isInGiftSubscription) {
        this.giftSubAnalytics.sendGiftPageCall(GIFT_SUBSCRIPTION_OFFERS_ANALYTICS_TAG, 'Gifter')
        return
      }
      this.sendPageCall()
      this.ensureLoggedIn()
    })
  }

  public isWrestlingOldEntitlements$ = this.experimentService.isInVariation(
    FEAT_OLD_ENTITLEMENTS_WRESTLING,
    FEAT_OLD_ENTITLEMENTS_WRESTLING_VARIATION
  )

  public isInSingleClickMobilePlansVariation$ = this.experimentService
    .isInVariation(FEAT_SINGLE_CLICK_MOBILE_PLANS_ID, FEAT_SINGLE_CLICK_MOBILE_PLANS_VARIATION)
    .pipe(shareReplay(1))

  public isInSingleClickDesktopPlansVariation$ = this.experimentService
    .isInVariation(FEAT_SINGLE_CLICK_DESKTOP_PLANS_ID, FEAT_SINGLE_CLICK_DESKTOP_PLANS_VARIATION)
    .pipe(shareReplay(1))

  public isIn2025PlansPageVariation1$ = this.experimentService.isInVariation(
    EXP_NEW_PLANS_PAGE_ID,
    EXP_NEW_PLANS_PAGE_VARIATION_1
  )

  public isIn2025PlansPageTopVerticalsVariation1$ = this.experimentService.isInVariation(
    EXP_NEW_PLANS_PAGE_TOP_VERTICALS_ID,
    EXP_NEW_PLANS_PAGE_TOP_VERTICALS_VARIATION_1
  )

  public isIn2025PlansPageVariation$ = someJoin([
    this.isIn2025PlansPageVariation1$,
    this.isIn2025PlansPageTopVerticalsVariation1$
  ])

  public isGwpActive$ = this.experimentService.isInVariation(FEAT_GWP_ID, FEAT_GWP_VARIATION)

  public isUnifiedLayout$ = this.experimentService.isInVariation(FEAT_UNIFIED_LAYOUT_ID, FEAT_UNIFIED_LAYOUT_VARIATION)

  public offers$ = this.offersService.offers$

  public legacyPlansConfig$ = combineLatest([
    this.productService.plans$,
    this.productService.selectedPlan$,
    this.verticalService.siteSettings$,
    this.productService.isWrestlingGiftPromoActive$,
    this.productService.isGwpActive$
  ]).pipe(
    map(([planData, selectedPlan, siteSetting, isInWrestlingGiftPromo, isGwpActive]) => {
      this.legacyPlans = [...planData.plans]
      return {
        selectedOffer: selectedPlan
          ? mapPlansToOfferSelectionCardModel([selectedPlan], siteSetting, isInWrestlingGiftPromo, isGwpActive)[0]
          : null,
        offers: mapPlansToOfferSelectionCardModel(planData.plans, siteSetting, isInWrestlingGiftPromo, isGwpActive)
      }
    })
  )

  // plan props change as the user selects a plan while on the old pricing architecture
  private legacyPlanProps$ = combineLatest([
    this.productService.savingsProp$,
    this.productService.selectedPlan$,
    this.productService.planProps$,
    this.isInSingleClickMobilePlansVariation$,
    this.productService.isWrestlingGiftPromoActive$,
    this.productService.isGwpActive$,
    this.verticalService.siteId$
  ]).pipe(
    map(
      ([
        savingsProp,
        selectedPlan,
        planProps,
        isInSingleClickMobileVariation,
        isWrestlingGiftPromoActive,
        isGwpActive,
        siteId
      ]) => {
        if (isInSingleClickMobileVariation) {
          return planProps
        }
        const shouldInjectWrestlingProp =
          selectedPlan?.yearly && isWrestlingGiftPromoActive && siteId === SiteIds.FLOWRESTLING
        const wrestlingPromo = shouldInjectWrestlingProp ? ['Includes a FREE Flo T-Shirt'] : []

        const shouldInjectGwpProp = selectedPlan?.yearly && isGwpActive && siteId === SiteIds.VARSITY
        const gwpPromo = shouldInjectGwpProp ? ['Includes a <strong>FREE</strong> VarsityTV Backpack'] : []

        // TODO: create a utility function for this
        const savings = selectedPlan?.yearly ? [savingsProp] : [] // if it isn't yearly, don't show savings
        const billedProps = selectedPlan ? [getBillingPlanLabel(selectedPlan)] : []
        return [...wrestlingPromo, ...gwpPromo, ...savings, billedProps, ...planProps]
      }
    )
  )

  // plan props change as the user selects a plan while on the new pricing architecture
  public newValueProps$ = combineLatest([
    this.offersService.selectedOffer$,
    this.isInSingleClickMobilePlansVariation$
  ]).pipe(
    map(([selectedOffer, isInSingleClickMobileVariation]) => {
      return isInSingleClickMobileVariation
        ? selectedOffer?.bulletPoints.filter(
            (bullet: TextModel) => !(bullet.text?.includes('Billed') || bullet.text?.includes('Save'))
          )
        : selectedOffer?.bulletPoints
    })
  )

  // value props for unified layout with conditional props
  public unifiedValueProps$ = combineLatest([this.offersService.selectedOffer$, this.isGwpActive$]).pipe(
    map(([selectedOffer, isGwpActive]): ReadonlyArray<Pick<TextModel, 'text' | 'style'>> => {
      const bulletPoints = selectedOffer?.bulletPoints || []
      const isAnnual = selectedOffer?.offerMetadata?.period === 'year'
      const iconStyle: TextModelStyle = isAnnual ? 'checkmark' : 'x-mark'

      const conditionalProps: Array<Pick<TextModel, 'text' | 'style'>> = []

      // Add GWP backpack prop when active
      if (isGwpActive) {
        conditionalProps.push({
          text: 'Includes a <strong>FREE</strong> VarsityTV Backpack',
          style: iconStyle
        })
      }

      return [...conditionalProps, ...bulletPoints]
    })
  )

  // only displayed on mobile because the value props are dynamic upon selection
  public valueProps$ = this.isWrestlingOldEntitlements$.pipe(
    switchMap(isWrestlingOldEntitlements =>
      iif(() => isWrestlingOldEntitlements, this.legacyPlanProps$, this.newValueProps$)
    )
  )

  // CXP-7255: Single-click mobile plans experiment
  public onOfferSelected(offer: OfferSelectionCardModel): void {
    combineLatest([
      this.isWrestlingOldEntitlements$,
      this.isInSingleClickDesktopPlansVariation$,
      this.isInSingleClickMobilePlansVariation$
    ])
      .pipe(take(1))
      .subscribe(
        ([isWrestlingOldEntitlements, isInSingleClickDesktopPlansVariation, isInSingleClickMobilePlansVariation]) => {
          if (isWrestlingOldEntitlements) {
            const selectedPlan = this.legacyPlans?.find(plan => plan.id === Number(offer.id))
            this.productService.updateSelectedPlan(selectedPlan)
          } else {
            this.offersService.updateSelectedOffer(offer)
          }
          if (isInSingleClickDesktopPlansVariation || isInSingleClickMobilePlansVariation) {
            this.goToNextPage()
          }
        }
      )
  }

  public goToNextPage(): void {
    combineLatest([
      this.ar.queryParams,
      this.isInGiftSubscription$,
      this.authService.loggedIn$,
      this.partialsDataService.hasPastDueSubscription$
    ])
      .pipe(take(1))
      .subscribe(([queryParams, isInGiftSubscription, isLoggedIn, isPastDue]) => {
        this.couponStatusUtility.hideCouponErrorSource.next(true)
        // TODO: use checkoutNavigation service
        let urlPath

        if (isPastDue) {
          urlPath = '/account/subscriptions'
        } else if (isInGiftSubscription) {
          urlPath = '/gift/info'
        } else if (!isLoggedIn) {
          urlPath = '/create-account'
        } else {
          urlPath = '/pay'
        }
        this.router.navigate([urlPath], { queryParams })
      })
  }

  private sendPageCall(): void {
    const qp = this.ar.snapshot.queryParamMap
    const routeData = this.ar.snapshot.data as unknown as FunnelRouteData
    this.segment.pageV2(LEGACY_PLANS_SEGMENT_TAG, getFunnelProperties(routeData, qp))
  }

  private ensureLoggedIn() {
    this.ar.queryParams
      .pipe(
        take(1),
        filter(queryParams => !!queryParams.embeddedPurchase)
      )
      .subscribe(_ => {
        // this ensures the cookies are properly set across the whole domain coming from embedded purchase
        this.authService.refreshViaToken()
      })
  }

  public shouldShowWrestlingGiftPromo$ = combineLatest([
    this.verticalService.siteId$,
    this.productService.isWrestlingGiftPromoActive$
  ]).pipe(
    map(([siteId, isGiftPromoActive]) => {
      if (siteId !== SiteIds.FLOWRESTLING) {
        return false
      }
      if (isGiftPromoActive) {
        return true
      }
      return false
    })
  )

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  readonly trackById = trackById
}
